import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, Navigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { Format } from '@gg/shared';
import { siteHome } from '@gg/shared/i18n';
import {
  Container,
  Cta,
  Disclosure,
  Eyebrow,
  H1,
  H2,
  H3,
  Section,
} from '@/components/site/primitives';
import { fill, useSiteContent } from '@/components/site/siteHelpers';
import { SITE_LOGO } from '@/components/site/SiteLayout';
import {
  ArrowIcon,
  BikeIcon,
  CalendarIcon,
  CheckBoxIcon,
  PinIcon,
  RunIcon,
  SoundOffIcon,
  SoundOnIcon,
  SwapIcon,
} from '@/components/site/siteIcons';
import { formatDateRange, formatDay, formatLastDay, formatNumber, formatTime } from '@/lib/dates';
import { isTeamFormat, localName } from '@/lib/formats';
import { formatPrice } from '@/lib/i18n';
import { useEvent } from '@/lib/queries';
import { earlyBirdEnd, groupSiteFormats, type SiteGroup } from '@/lib/siteFormats';

type Content = ReturnType<typeof useSiteContent<typeof siteHome>>;

/**
 * Public home page (gravelgames.fr/), docs/design/site/Main.dc.html. Every "Je m'engage" opens
 * the format selection (/formats). Formats, prices and sale dates come from the API.
 */
export function Home() {
  const { search } = useLocation();
  const c = useSiteContent(siteHome);
  const { data: event } = useEvent();

  useEffect(() => {
    document.title = c.documentTitle;
  }, [c.documentTitle]);

  // Links sent before the site existed opened the format selection at the root: an invitation
  // (`/?ref=…`) or "add a race" from the profile (`/?add=1`). Keep them working.
  const params = new URLSearchParams(search);
  if (params.has('ref') || params.has('add')) return <Navigate to={`/formats${search}`} replace />;

  const formats = event?.formats.filter((f) => f.isActive) ?? [];
  const ebEnd = earlyBirdEnd(formats);
  const dates =
    event?.startsAt &&
    `${formatDateRange(event.startsAt, event.endsAt)} ${new Date(event.startsAt).getFullYear()}`;

  return (
    <>
      <Hero c={c} dates={dates || null} earlyBird={ebEnd ? formatLastDay(ebEnd) : null} />
      <Stats c={c} count={formats.length} />
      <Concept c={c} />
      <Events c={c} />
      <Crew c={c} />
      <Info c={c} />
      <Faq c={c} />
      <FinalCta c={c} dates={dates || null} />
    </>
  );
}

function Hero({
  c,
  dates,
  earlyBird,
}: {
  c: Content;
  dates: string | null;
  earlyBird: string | null;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const [muted, setMuted] = useState(true);
  const [motion, setMotion] = useState(true);

  useEffect(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    setMotion(!reduce);
    const v = video.current;
    if (!v || reduce) return;
    v.muted = true;
    v.play()?.catch(() => {});
  }, []);

  const toggleSound = () => {
    const v = video.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    if (!v.muted) v.play()?.catch(() => {});
  };

  return (
    <section
      id="accueil"
      className="relative -mt-[76px] flex min-h-[720px] items-end overflow-hidden bg-gradient-to-br from-site-night via-site-earth to-site-rust px-5 pt-35 pb-24 text-site-cream md:min-h-[900px] md:px-10 md:pt-45"
    >
      <img
        src="/site/hero.jpg"
        alt=""
        aria-hidden="true"
        className="absolute inset-0 size-full object-cover"
      />
      {motion && (
        <video
          ref={video}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster="/site/hero.jpg"
          aria-hidden="true"
          className="absolute inset-0 size-full object-cover"
        >
          <source src="/site/hero.mp4" type="video/mp4" />
        </video>
      )}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-t from-site-night-deep/95 via-site-night/60 to-site-night/30"
      />
      {motion && (
        <button
          type="button"
          onClick={toggleSound}
          aria-label={muted ? c.hero.soundOn : c.hero.soundOff}
          aria-pressed={!muted}
          className="absolute top-25 right-5 inline-flex size-11 items-center justify-center rounded-full border border-site-cream/40 bg-site-night/40 text-site-cream md:right-10"
        >
          {muted ? <SoundOffIcon size={20} /> : <SoundOnIcon size={20} />}
        </button>
      )}
      <Container>
        <img
          src={SITE_LOGO}
          alt=""
          aria-hidden="true"
          className="mb-10 block h-16 w-auto max-w-full md:h-24"
        />
        <p className="m-0 mb-5.5 font-site-display text-[13px] font-semibold tracking-[0.28em] text-site-clay-light uppercase">
          {c.hero.open}
          {earlyBird && ` · ${fill(c.hero.earlyBird, { date: earlyBird })}`}
        </p>
        <H1 className="max-w-[900px] leading-none! text-site-cream">{c.hero.title}</H1>
        <p className="m-0 mt-5.5 text-lg text-site-cream/90 md:text-[22px]">{c.hero.sub}</p>
        <div className="mt-6.5 flex flex-wrap gap-x-8 gap-y-3.5 font-site-display text-base tracking-[0.16em] text-site-cream/90 uppercase">
          {dates && (
            <span className="inline-flex items-center gap-2.5">
              <CalendarIcon className="text-site-clay-light" />
              {dates}
            </span>
          )}
          <span className="inline-flex items-center gap-2.5">
            <PinIcon className="text-site-clay-light" />
            {c.hero.place}
          </span>
        </div>
        <div className="mt-10 flex flex-wrap gap-3.5">
          <Cta to="/formats" size="lg">
            {c.hero.engage}
            <ArrowIcon />
          </Cta>
          <Cta to="/#epreuves" variant="ghost" size="lg">
            {c.hero.seeEvents}
          </Cta>
        </div>
      </Container>
    </section>
  );
}

function Stats({ c, count }: { c: Content; count: number }) {
  return (
    <div className="border-t border-site-cream/10 bg-site-night text-site-cream">
      <div className="mx-auto grid max-w-[1320px] grid-cols-2 px-5 md:px-10 lg:grid-cols-4">
        {c.stats.map((s, i) => (
          <div
            key={s.value}
            className={`py-7.5 pr-4 ${i % 2 === 1 ? 'pl-4' : ''} lg:px-6 lg:first:pl-0 ${i < 3 ? 'lg:border-r lg:border-site-cream/10' : ''}`}
          >
            <p className="m-0 font-site-display text-[28px] leading-none font-bold md:text-[40px]">
              {/* "9 formats": the count of formats on the site, hidden until the API answers. */}
              {s.value.includes('{{count}}')
                ? count > 0
                  ? fill(s.value, { count })
                  : '…'
                : s.value}
            </p>
            <p className="m-0 mt-2 text-sm text-site-cream/70">{s.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

const pillarIcons = { bike: BikeIcon, run: RunIcon, both: SwapIcon } as const;

function Concept({ c }: { c: Content }) {
  return (
    <Section id="concept" className="site-topo">
      <Container className="flex flex-wrap items-start gap-16">
        <div className="min-w-0 flex-[1_1_460px]">
          <Eyebrow>{c.concept.eyebrow}</Eyebrow>
          <H2>
            {c.concept.title[0]}
            <br />
            {c.concept.title[1]}
          </H2>
          <p className="m-0 mt-4.5 font-site-display text-lg tracking-[0.2em] text-site-bronze uppercase">
            {c.concept.motto}
          </p>
        </div>
        <div className="flex min-w-0 flex-[1_1_460px] flex-col gap-5 text-lg leading-relaxed text-site-ink-body">
          {c.concept.paragraphs.map((p) => (
            <p key={p} className="m-0">
              {p}
            </p>
          ))}
        </div>
      </Container>
      <Container className="mt-18 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {c.concept.pillars.map((p) => {
          const Icon = pillarIcons[p.icon];
          const dark = p.icon === 'both';
          return (
            <div
              key={p.title}
              className={`p-7.5 ${dark ? 'bg-site-night text-site-cream' : 'border border-site-line bg-site-card'}`}
            >
              <Icon size={36} className={dark ? 'text-site-clay-light' : 'text-site-clay'} />
              <H3 className="mt-4.5 text-[26px]">{p.title}</H3>
              <p
                className={`m-0 mt-2.5 text-base leading-normal ${dark ? 'text-site-cream/80' : 'text-site-ink-muted'}`}
              >
                {p.text}
              </p>
            </div>
          );
        })}
      </Container>
    </Section>
  );
}

function Events({ c }: { c: Content }) {
  const { data: event, isError } = useEvent();
  const groups = event ? groupSiteFormats(event.formats) : [];
  const ebEnd = event ? earlyBirdEnd(event.formats) : null;

  return (
    <Section id="epreuves" tone="deep">
      <Container>
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Eyebrow>{fill(c.events.eyebrow, { edition: event?.edition ?? '' })}</Eyebrow>
            <H2>{c.events.title}</H2>
            <p className="m-0 mt-4 text-[17px] text-site-ink-muted">{c.events.sub}</p>
          </div>
          {ebEnd && (
            <p className="m-0 flex items-center gap-3 bg-site-night px-4.5 py-3 text-sm text-site-cream">
              <span
                aria-hidden="true"
                className="inline-block size-2 rounded-full bg-site-clay-light"
              />
              {fill(c.events.earlyBird, { date: formatLastDay(ebEnd) })}
            </p>
          )}
        </div>

        {isError && <p className="mt-12 text-site-ink-muted">{c.events.error}</p>}
        {!event && !isError && (
          <p role="status" className="mt-12 text-site-ink-muted">
            {c.events.loading}
          </p>
        )}

        <div className="mt-12 grid gap-5 md:grid-cols-2">
          {groups.map((g) => (
            <EventCard key={g.group} c={c} group={g.group} formats={g.formats} />
          ))}
        </div>

        <div className="mt-5.5 flex flex-wrap gap-x-7 gap-y-2.5 text-sm leading-normal text-site-ink-muted">
          <span>
            <strong className="text-site-clay-deep">{c.events.legendPrice}</strong>{' '}
            {c.events.legend}
          </span>
          <span>{c.events.documents}</span>
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-between gap-6 bg-site-night px-6 py-8 text-site-cream md:px-9">
          <div className="flex min-w-0 flex-[1_1_520px] items-start gap-5">
            <CheckBoxIcon size={40} className="flex-none text-site-clay-light" />
            <div>
              <p className="m-0 font-site-display text-[28px] leading-[1.05] font-bold uppercase">
                {c.events.check.title}
              </p>
              <p className="m-0 mt-2 text-base leading-normal text-site-cream/80">
                {c.events.check.text}
              </p>
            </div>
          </div>
          <Cta to="/conditions#verifier" size="md" className="min-h-13.5 text-[15px]">
            {c.events.check.cta}
            <ArrowIcon />
          </Cta>
        </div>
      </Container>
    </Section>
  );
}

function EventCard({ c, group, formats }: { c: Content; group: SiteGroup; formats: Format[] }) {
  const { t } = useTranslation();
  const copy = c.events.groups[group];
  const dark = group === 'team';
  const muted = dark ? 'text-site-cream/65' : 'text-site-ink-subtle';
  const line = dark ? 'border-site-cream/15' : 'border-site-line-soft';

  return (
    <article
      className={`flex flex-col ${dark ? 'bg-site-night text-site-cream' : 'border border-site-line bg-site-card'}`}
    >
      <div className="px-7 pt-7">
        <p
          className={`m-0 font-site-display text-xs tracking-[0.24em] uppercase ${dark ? 'text-site-clay-light' : 'text-site-bronze'}`}
        >
          {copy.kicker}
        </p>
        <H3 className="mt-2.5 text-[32px]">{copy.title}</H3>
        <p
          className={`m-0 mt-2.5 text-[15px] leading-normal ${dark ? 'text-site-cream/80' : 'text-site-ink-muted'}`}
        >
          {copy.text}
        </p>
      </div>
      <div className="flex flex-1 flex-col px-7 py-5.5">
        <ul className={`m-0 list-none border-b p-0 ${line}`}>
          {formats.map((f) => (
            <li
              key={f.id}
              className={`flex items-center justify-between gap-3 border-t py-3.5 ${line}`}
            >
              <div>
                <p className="m-0 font-site-display text-[22px] leading-tight font-semibold">
                  {localName(f.name, f.nameEn)}
                </p>
                <p className={`m-0 mt-1 text-[13px] ${muted}`}>{formatMeta(c, f)}</p>
              </div>
              <Price c={c} f={f} dark={dark} t={t} />
            </li>
          ))}
        </ul>
        {'note' in copy && (
          <p
            className={`m-0 mt-4 text-[13px] leading-normal ${dark ? 'text-site-cream/75' : 'text-site-ink-muted'}`}
          >
            {copy.note}
          </p>
        )}
        {'perks' in copy && (
          <ul className="m-0 mt-3.5 flex list-disc flex-col gap-1.5 pl-4.5 text-sm text-site-cream/80">
            {copy.perks.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        )}
      </div>
      <Link
        to="/conditions"
        className={`mx-7 mb-4 inline-flex min-h-8 items-center text-sm ${dark ? 'text-site-clay-pale hover:text-site-cream' : 'text-site-clay-deep hover:text-site-ink'}`}
      >
        {c.events.conditions}
      </Link>
      <Cta to="/formats" className="mx-7 mb-7">
        {c.events.engage}
      </Cta>
    </article>
  );
}

/** "900 m D+ · Dimanche 4 avril · 08:00": whatever the format has. */
function formatMeta(c: Content, f: Format): string {
  return [
    f.elevationM ? fill(c.events.elevation, { value: formatNumber(f.elevationM) }) : null,
    f.startsAt ? formatDay(f.startsAt, 'long') : null,
    f.startsAt ? formatTime(f.startsAt) : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

function Price({
  c,
  f,
  dark,
  t,
}: {
  c: Content;
  f: Format;
  dark: boolean;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  const muted = dark ? 'text-site-cream/65' : 'text-site-ink-subtle';
  const strong = `block font-site-display text-[22px] ${dark ? 'text-site-clay-pale' : 'text-site-clay-deep'}`;
  // Every team member pays their own place (decision 0021): team prices are per person.
  const per = isTeamFormat(f) ? ` ${c.events.perPerson}` : '';

  // Before sales open (or between tiers), the price of the tier opening next, not its date.
  const priceCents = f.currentTier?.priceCents ?? f.nextPriceCents;
  let status: ReactNode = null;
  if (f.currentTier && f.remainingSpots === 0) status = t('formats.soldOut');
  else if (priceCents === null) status = t('formats.closed');

  return (
    <p className={`m-0 text-right text-[13px] whitespace-nowrap ${muted}`}>
      {priceCents !== null && (
        <strong className={strong}>
          {formatPrice(priceCents)}
          {per && <span className={`text-[13px] font-normal ${muted}`}>{per}</span>}
        </strong>
      )}
      {status ??
        (priceCents !== null && f.standardPriceCents !== null && priceCents < f.standardPriceCents
          ? fill(c.events.then, { price: formatPrice(f.standardPriceCents) })
          : null)}
    </p>
  );
}

function Crew({ c }: { c: Content }) {
  const [before, after] = c.crew.rulesText.split('{{email}}');
  return (
    <Section id="embarque" tone="dark" topo>
      <Container className="flex flex-wrap items-center gap-16">
        <div className="min-w-0 flex-[1_1_480px]">
          <Eyebrow tone="clay">{c.crew.eyebrow}</Eyebrow>
          <H2 className="text-site-cream">{c.crew.title}</H2>
          <p className="m-0 mt-5.5 text-lg leading-relaxed text-site-cream/80">{c.crew.text}</p>
          <ol className="m-0 mt-10 list-none border-b border-site-cream/15 p-0">
            {c.crew.steps.map((s, i) => (
              <li key={s.title} className="flex gap-5 border-t border-site-cream/15 py-5">
                <span className="min-w-9 font-site-display text-[30px] leading-none font-bold text-site-clay-light">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div>
                  <p className="m-0 font-site-display text-xl tracking-[0.06em] uppercase">
                    {s.title}
                  </p>
                  <p className="m-0 mt-1.5 text-[15px] text-site-cream/70">{s.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
        <div className="min-w-0 flex-[1_1_400px] border border-site-cream/20 bg-site-night/60 p-7 md:p-9">
          <p className="m-0 font-site-display text-[13px] font-semibold tracking-[0.24em] text-site-clay-light uppercase">
            {c.crew.prizesEyebrow}
          </p>
          <p className="m-0 mt-3.5 font-site-display text-[30px] leading-[1.1] font-bold uppercase">
            {c.crew.prizesTitle}
          </p>
          <p className="m-0 mt-3.5 text-base leading-relaxed text-site-cream/80">
            {c.crew.prizesText}
          </p>
          <div className="mt-7 border-t border-site-cream/15 pt-5.5">
            <p className="m-0 font-site-display text-[13px] font-semibold tracking-[0.24em] text-site-cream/70 uppercase">
              {c.crew.rulesTitle}
            </p>
            <p className="m-0 mt-2.5 text-[15px] leading-relaxed text-site-cream/80">
              {before}
              <a
                href={`mailto:${c.crew.email}`}
                className="text-site-cream hover:text-site-clay-light"
              >
                {c.crew.email}
              </a>
              {after}
            </p>
            <address className="mt-2 text-[15px] leading-relaxed not-italic">
              {c.crew.address.map((l) => (
                <span key={l} className="block">
                  {l}
                </span>
              ))}
            </address>
          </div>
        </div>
      </Container>
    </Section>
  );
}

/** "**bold** rest": the programme lines' only markup. */
function Rich({ text }: { text: string }) {
  const m = /^\*\*(.+?)\*\*(.*)$/.exec(text);
  if (!m) return <>{text}</>;
  return (
    <>
      <strong>{m[1]}</strong>
      {m[2]}
    </>
  );
}

function Info({ c }: { c: Content }) {
  return (
    <Section id="infos">
      <Container>
        <Eyebrow>{c.info.eyebrow}</Eyebrow>
        <H2>{c.info.title}</H2>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {c.info.cards.map((card) => (
            <div key={card.title} className="border-t-[3px] border-site-ink bg-site-card p-7">
              <H3 className="text-[22px]">{card.title}</H3>
              <p className="m-0 mt-3 text-base leading-normal text-site-ink-body">
                {card.lines.map((l, i) => (
                  <span key={l}>
                    {i > 0 && <br />}
                    <Rich text={l} />
                  </span>
                ))}
              </p>
            </div>
          ))}
        </div>
        <div className="site-topo relative mt-5 flex min-h-[320px] flex-col items-center justify-center gap-4 border border-site-line-strong bg-site-ground-map p-6 text-center md:min-h-[420px]">
          <PinIcon size={44} strokeWidth={1.8} className="text-site-clay" />
          <p className="m-0 font-site-display text-2xl font-bold uppercase">{c.info.mapTitle}</p>
          <p className="m-0 text-[15px] text-site-ink-body">{c.info.mapPlace}</p>
          <Cta
            href={c.info.mapUrl}
            variant="night"
            className="min-h-12 px-5.5"
            rel="noopener"
            target="_blank"
          >
            {c.info.mapCta}
          </Cta>
        </div>
      </Container>
    </Section>
  );
}

function Faq({ c }: { c: Content }) {
  return (
    <Section id="faq" className="pt-0! md:pt-0!">
      <div className="relative mx-auto max-w-[920px]">
        <Eyebrow>{c.faq.eyebrow}</Eyebrow>
        <H2 size="md">{c.faq.title}</H2>
        <div className="mt-9 border-t border-site-line-strong">
          {c.faq.items.map((item, i) => (
            <Disclosure key={item.q} summary={item.q} defaultOpen={i === 0}>
              <p className="m-0">{item.a}</p>
            </Disclosure>
          ))}
        </div>
      </div>
    </Section>
  );
}

function FinalCta({ c, dates }: { c: Content; dates: string | null }) {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-site-night via-site-earth to-site-rust px-5 py-18 text-site-cream md:px-10 md:py-28">
      <div aria-hidden="true" className="site-topo absolute inset-0 opacity-60" />
      <Container className="flex flex-wrap items-center justify-between gap-8">
        <div>
          <H2 className="text-site-cream md:text-[64px]">{c.final.title}</H2>
          <p className="m-0 mt-4 text-lg text-site-cream/85">
            {[dates, c.final.place].filter(Boolean).join(' · ')}
          </p>
        </div>
        <Cta to="/formats" size="lg" className="min-h-15 px-9 text-[17px]">
          {c.final.engage}
          <ArrowIcon />
        </Cta>
      </Container>
    </section>
  );
}
